import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Star, Users, MessageSquare, BarChart3, CheckCircle2, Grid3x3 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  processSubmissionAnswers,
  groupAnswersByType,
  formatAnswerValue,
  processMatrixAnswer,
  type ProcessedAnswer
} from "@/utils/jotformQuestionMapper";
import {
  mapControlMatrixField,
  getRatingColor as getFieldRatingColor,
  getRatingBgColor
} from "@/utils/jotformFieldMapper";

interface AnswersDisplayV2Props {
  answers: Record<string, any>;
  mappedAnswers?: any[];
}

export function AnswersDisplayV2({ answers, mappedAnswers }: AnswersDisplayV2Props) {
  const [dataSource, setDataSource] = useState<'raw' | 'mapped'>('mapped');

  const processedAnswers = processSubmissionAnswers(answers);
  const groupedAnswers = groupAnswersByType(processedAnswers);

  const getRatingColor = (rating: number): string => {
    if (rating >= 8) return 'bg-green-100 text-green-800 border-green-300';
    if (rating >= 6) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const renderRatingAnswer = (answer: ProcessedAnswer) => {
    const rating = parseFloat(answer.answerValue);
    return (
      <div key={answer.questionId} className="border-b pb-4 last:border-0">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-700 mb-2">
              {answer.mappedName}
            </p>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className={`${getRatingColor(rating)} px-4 py-2 text-lg font-bold`}>
                {rating}/10
              </Badge>
              <div className="flex gap-1">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-6 h-6 rounded ${
                      i < rating ? 'bg-primary' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
          <Badge variant="secondary" className="ml-4">
            <Star className="w-3 h-3 mr-1" />
            Rating
          </Badge>
        </div>
      </div>
    );
  };

  const renderDemographicAnswer = (answer: ProcessedAnswer) => {
    return (
      <div key={answer.questionId} className="border-b pb-3 last:border-0">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-700">
              {answer.questionText}
            </p>
            <p className="mt-1 text-sm text-gray-900">
              {formatAnswerValue(answer.answerValue)}
            </p>
          </div>
          <Badge variant="outline" className="ml-4">
            <Users className="w-3 h-3 mr-1" />
            Demográfico
          </Badge>
        </div>
      </div>
    );
  };

  const renderChoiceAnswer = (answer: ProcessedAnswer) => {
    return (
      <div key={answer.questionId} className="border-b pb-3 last:border-0">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-700">
              {answer.questionText}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <p className="text-sm text-gray-900 font-medium">
                {formatAnswerValue(answer.answerValue)}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="ml-4 bg-blue-50 text-blue-700 border-blue-200">
            Opción
          </Badge>
        </div>
      </div>
    );
  };

  const renderTextAnswer = (answer: ProcessedAnswer) => {
    return (
      <div key={answer.questionId} className="border-b pb-4 last:border-0">
        <div className="flex items-start justify-between mb-2">
          <p className="text-sm font-semibold text-gray-700 flex-1">
            {answer.questionText}
          </p>
          <Badge variant="outline" className="ml-4 bg-purple-50 text-purple-700 border-purple-200">
            <MessageSquare className="w-3 h-3 mr-1" />
            Texto
          </Badge>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg border-l-4 border-purple-300">
          <p className="text-sm text-gray-700 italic">"{formatAnswerValue(answer.answerValue)}"</p>
        </div>
      </div>
    );
  };

  const renderMatrixAnswer = (answer: ProcessedAnswer) => {
    // Intentar usar el nuevo mapper si tenemos el campo original con metadata completa
    const originalField = answer.metadata?.originalField;
    
    if (originalField && originalField.type === 'control_matrix') {
      const mappedMatrix = mapControlMatrixField(answer.questionId, originalField);
      
      if (mappedMatrix && mappedMatrix.rows.length > 0) {
        return (
          <div key={answer.questionId} className="border-b pb-4 last:border-0">
            <div className="mb-3">
              <div className="flex items-start justify-between mb-4">
                <p className="text-sm font-semibold text-gray-700 flex-1">
                  {answer.questionText || 'Evaluación de Matriz'}
                </p>
                <Badge variant="outline" className="ml-4 bg-purple-50 text-purple-700 border-purple-200">
                  <Grid3x3 className="w-3 h-3 mr-1" />
                  Matriz
                </Badge>
              </div>
            </div>
            <div className="space-y-3">
              {mappedMatrix.rows.map((row) => {
                const rating = Number(row.answer);
                const isValidRating = !isNaN(rating) && rating >= 0 && rating <= 10;

                return (
                  <div
                    key={row.id}
                    className={`p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                      isValidRating ? getRatingBgColor(rating) : 'bg-muted/30 border-muted'
                    }`}
                  >
                    <div className="flex justify-between items-center gap-4">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground leading-relaxed">{row.text}</p>
                      </div>
                      <div className="flex-shrink-0">
                        <Badge
                          variant="outline"
                          className={`text-lg font-bold px-4 py-1 ${isValidRating ? getFieldRatingColor(rating) : ''}`}
                        >
                          {row.answer}/10
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
    }

    // Fallback al renderizado anterior si no podemos usar el nuevo mapper
    const matrixData = processMatrixAnswer(answer.answerValue);

    return (
      <div key={answer.questionId} className="border-b pb-4 last:border-0">
        <div className="mb-3">
          <div className="flex items-start justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700 flex-1">
              {answer.questionText}
            </p>
            <Badge variant="outline" className="ml-4 bg-orange-50 text-orange-700 border-orange-200">
              <BarChart3 className="w-3 h-3 mr-1" />
              Matriz (Legacy)
            </Badge>
          </div>
        </div>
        <div className="grid gap-2">
          {matrixData.length > 0 ? (
            matrixData.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <span className="text-sm font-medium text-gray-700">{item.area}</span>
                <Badge variant="outline" className={getRatingColor(item.rating)}>
                  {item.rating}/10
                </Badge>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 italic">Sin valoraciones registradas</p>
          )}
        </div>
      </div>
    );
  };

  const renderOtherAnswer = (answer: ProcessedAnswer) => {
    return (
      <div key={answer.questionId} className="border-b pb-3 last:border-0">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-700">
              {answer.questionText}
            </p>
            <p className="mt-1 text-sm text-gray-900">
              {formatAnswerValue(answer.answerValue)}
            </p>
          </div>
          <Badge variant="outline" className="ml-4">
            Otro
          </Badge>
        </div>
      </div>
    );
  };
  const renderMappedAnswers = () => {
    if (!mappedAnswers || mappedAnswers.length === 0) {
      return (
        <div className="p-8 text-center text-muted-foreground">
          No hay respuestas mapeadas disponibles
        </div>
      );
    }

    // Filtrar por tipo
    const matricesTypes = ['control_matrix', 'control_rating', 'control_scale', 'control_head'];
    const textosTypes = ['control_textarea', 'control_textbox', 'control_dropdown', 'control_radio', 'control_checkbox', 'control_email', 'control_phone', 'control_number', 'control_fullname', 'control_address'];
    
    const matrices = mappedAnswers.filter(q => 
      matricesTypes.includes(q.question_type) && q.question_type !== 'control_text' && q.question_type !== 'control_image'
    );
    
    const textos = mappedAnswers.filter(q => 
      textosTypes.includes(q.question_type) && q.question_type !== 'control_text' && q.question_type !== 'control_image'
    );

    const renderMappedQuestion = (question: any, index: number) => {
      const questionType = question.question_type;
      const questionText = question.question_text || question.question_name || 'Sin título';
      const answer = question.answer;

      // Ocultar campos control_text y control_image
      if (questionType === 'control_text' || questionType === 'control_image') {
        return null;
      }

      // Control Matrix
      if (questionType === 'control_matrix' && question.rows && Array.isArray(question.rows)) {
        return (
          <div key={index} className="border-b pb-4 last:border-0">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <p className="text-[10px] text-gray-400 mb-1">{question.question_id}</p>
                <p className="text-sm font-semibold text-foreground">{questionText}</p>
              </div>
              <Badge variant="outline" className="ml-4">
                Matriz
              </Badge>
            </div>
            <div className="space-y-2">
              {question.rows.map((row: any, rowIndex: number) => {
                const rating = Number(row.answer);
                const isValidRating = !isNaN(rating) && rating >= 0 && rating <= 10;
                
                return (
                  <div 
                    key={rowIndex} 
                    className="p-3 rounded-lg bg-gray-50 border border-gray-200"
                  >
                    <div className="flex justify-between items-center gap-4">
                      <div className="flex-1">
                        <p className="text-sm text-foreground">
                          {row.text}
                        </p>
                      </div>
                      {isValidRating && (
                        <div className="flex-shrink-0">
                          <Badge 
                            variant="outline" 
                            className={`text-base font-semibold px-3 py-0.5 ${
                              rating >= 8 ? 'bg-green-50 text-green-700 border-green-300' :
                              rating >= 6 ? 'bg-yellow-50 text-yellow-700 border-yellow-300' :
                              'bg-red-50 text-red-700 border-red-300'
                            }`}
                          >
                            {row.answer}/10
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      // Control Rating / Scale
      if ((questionType === 'control_rating' || questionType === 'control_scale') && answer) {
        const rating = parseFloat(answer);
        const isValid = !isNaN(rating);

        return (
          <div key={index} className="border-b pb-4 last:border-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <p className="text-[10px] text-gray-400 mb-1">{question.question_id}</p>
                <p className="text-sm font-semibold text-foreground">{questionText}</p>
              </div>
              <Badge variant="outline" className="ml-4">Rating</Badge>
            </div>
            {isValid && (
              <div className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className={`text-base font-semibold px-3 py-1 ${
                    rating >= 8 ? 'bg-green-50 text-green-700 border-green-300' :
                    rating >= 6 ? 'bg-yellow-50 text-yellow-700 border-yellow-300' :
                    'bg-red-50 text-red-700 border-red-300'
                  }`}
                >
                  {rating}/10
                </Badge>
                <div className="flex gap-0.5">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-4 h-4 rounded-sm ${i < rating ? 'bg-primary' : 'bg-gray-200'}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }

      // Control Dropdown / Radio
      if ((questionType === 'control_dropdown' || questionType === 'control_radio') && answer) {
        return (
          <div key={index} className="border-b pb-4 last:border-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <p className="text-[10px] text-gray-400 mb-1">{question.question_id}</p>
                <p className="text-sm font-semibold text-foreground">{questionText}</p>
              </div>
              <Badge variant="outline" className="ml-4">
                Opción
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <p className="text-sm text-foreground font-medium">{formatAnswerValue(answer)}</p>
            </div>
          </div>
        );
      }

      // Control Textarea / Textbox / Text
      if ((questionType === 'control_textarea' || questionType === 'control_textbox' || questionType === 'control_text') && answer) {
        return (
          <div key={index} className="border-b pb-4 last:border-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <p className="text-[10px] text-gray-400 mb-1">{question.question_id}</p>
                <p className="text-sm font-semibold text-foreground">{questionText}</p>
              </div>
              <Badge variant="outline" className="ml-4">
                Texto
              </Badge>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-foreground">{formatAnswerValue(answer)}</p>
            </div>
          </div>
        );
      }

      // Control Checkbox
      if (questionType === 'control_checkbox' && answer) {
        const values = Array.isArray(answer) ? answer : [answer];
        return (
          <div key={index} className="border-b pb-4 last:border-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <p className="text-[10px] text-gray-400 mb-1">{question.question_id}</p>
                <p className="text-sm font-semibold text-foreground">{questionText}</p>
              </div>
              <Badge variant="outline" className="ml-4">
                Multiple
              </Badge>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {values.map((val: any, idx: number) => (
                <Badge key={idx} variant="outline">
                  {formatAnswerValue(val)}
                </Badge>
              ))}
            </div>
          </div>
        );
      }

      // Control Email / Phone / Number
      if ((questionType === 'control_email' || questionType === 'control_phone' || questionType === 'control_number') && answer) {
        return (
          <div key={index} className="border-b pb-4 last:border-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <p className="text-[10px] text-gray-400 mb-1">{question.question_id}</p>
                <p className="text-sm font-semibold text-foreground">{questionText}</p>
              </div>
              <Badge variant="outline" className="ml-4">
                {questionType.replace('control_', '')}
              </Badge>
            </div>
            <p className="text-sm text-foreground font-medium">{formatAnswerValue(answer)}</p>
          </div>
        );
      }

      // Default fallback para otros tipos
      return (
        <div key={index} className="border-b pb-4 last:border-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <p className="text-[10px] text-gray-400 mb-1">{question.question_id}</p>
              <p className="text-sm font-semibold text-foreground">{questionText}</p>
            </div>
            <Badge variant="outline" className="ml-4">
              {questionType}
            </Badge>
          </div>
          <p className="text-sm text-foreground">
            {typeof answer === 'object' ? JSON.stringify(answer, null, 2) : String(answer || 'Sin respuesta')}
          </p>
        </div>
      );
    };

    return (
      <Tabs defaultValue="textos" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="textos" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Textos ({textos.length})
          </TabsTrigger>
          <TabsTrigger value="matrices" className="flex items-center gap-2">
            <Grid3x3 className="w-4 h-4" />
            Comportamientos ({matrices.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="textos" className="space-y-4 mt-4">
          {textos.length > 0 ? (
            textos.map((question, index) => renderMappedQuestion(question, index))
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No hay textos disponibles
            </div>
          )}
        </TabsContent>

        <TabsContent value="matrices" className="space-y-4 mt-4">
          {matrices.length > 0 ? (
            matrices.map((question, index) => renderMappedQuestion(question, index))
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No hay matrices disponibles
            </div>
          )}
        </TabsContent>
      </Tabs>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Respuestas del Formulario</CardTitle>
          <Select value={dataSource} onValueChange={(value: 'raw' | 'mapped') => setDataSource(value)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mapped" disabled={!mappedAnswers || mappedAnswers.length === 0}>
                Datos Mapeados {!mappedAnswers || mappedAnswers.length === 0 ? '(N/A)' : ''}
              </SelectItem>
              <SelectItem value="raw">Datos Raw</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {dataSource === 'mapped' ? (
          renderMappedAnswers()
        ) : !hasContent ? (
          <div className="p-8 text-center text-muted-foreground">
            No hay respuestas disponibles
          </div>
        ) : (
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="all">
                Todas ({processedAnswers.length})
              </TabsTrigger>
              <TabsTrigger value="ratings" disabled={groupedAnswers.ratings.length === 0}>
                <Star className="w-4 h-4 mr-1" />
                Ratings ({groupedAnswers.ratings.length})
              </TabsTrigger>
              <TabsTrigger value="demographics" disabled={groupedAnswers.demographics.length === 0}>
                <Users className="w-4 h-4 mr-1" />
                Perfil ({groupedAnswers.demographics.length})
              </TabsTrigger>
              <TabsTrigger value="choices" disabled={groupedAnswers.choices.length === 0}>
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Opciones ({groupedAnswers.choices.length})
              </TabsTrigger>
              <TabsTrigger value="texts" disabled={groupedAnswers.texts.length === 0}>
                <MessageSquare className="w-4 h-4 mr-1" />
                Textos ({groupedAnswers.texts.length})
              </TabsTrigger>
              <TabsTrigger value="matrix" disabled={groupedAnswers.matrix.length === 0}>
                <BarChart3 className="w-4 h-4 mr-1" />
                Matriz ({groupedAnswers.matrix.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-4 mt-4">
              {processedAnswers.map((answer) => {
                switch (answer.answerType) {
                  case 'rating':
                    return renderRatingAnswer(answer);
                  case 'demographic':
                    return renderDemographicAnswer(answer);
                  case 'choice':
                    return renderChoiceAnswer(answer);
                  case 'text':
                    return renderTextAnswer(answer);
                  case 'matrix':
                    return renderMatrixAnswer(answer);
                  default:
                    return renderOtherAnswer(answer);
                }
              })}
            </TabsContent>

            <TabsContent value="ratings" className="space-y-4 mt-4">
              {groupedAnswers.ratings.map(renderRatingAnswer)}
            </TabsContent>

            <TabsContent value="demographics" className="space-y-4 mt-4">
              {groupedAnswers.demographics.map(renderDemographicAnswer)}
            </TabsContent>

            <TabsContent value="choices" className="space-y-4 mt-4">
              {groupedAnswers.choices.map(renderChoiceAnswer)}
            </TabsContent>

            <TabsContent value="texts" className="space-y-4 mt-4">
              {groupedAnswers.texts.map(renderTextAnswer)}
            </TabsContent>

            <TabsContent value="matrix" className="space-y-4 mt-4">
              {groupedAnswers.matrix.map(renderMatrixAnswer)}
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}
